import React, {
  useEffect,
  useMemo,
  useState,
  useRef,
  useCallback
} from 'react';
import {
  getService,
  setGeneratingExtraction,
  setService,
  useAppDispatch,
  useAppSelector
} from 'app/redux';
import styles from './NDAGenerator.module.scss';
import { GeneratingExtraction } from 'app/components';
import { notify } from '@zurich-base-ui/components.toast';
import { displayToastError } from 'app/utils/functions';
import { ndaApi } from 'app/api/ndaGeneratorApi';
import { CreateMode } from './parts/CreateMode';
import ReviewMode from './parts/ReviewMode';
import type { FormDataShape, NdaType, TemplateOption } from './parts/types';
import { ChatPanel } from './parts/ChatPanel';
import { Header } from './parts/Header';
import { useNdaChat } from './parts/useNdaChat';
import type { GenerateNDAResponse } from 'app/api/ndaGeneratorApi';

const requiredKeys: Array<keyof FormDataShape> = [
  'xCompany',
  'xAddress',
  'xMail',
  'zurichAddress',
  'zurichMail',
  'project',
  'date',
  'xSigner',
  'zurichSigner'
];

export const NDAGenerator: React.FC = () => {
  const dispatch = useAppDispatch();
  const currentService = useAppSelector(getService);
  useEffect(() => {
    if (!currentService) dispatch(setService('nda-generator'));
  }, [currentService, dispatch]);

  const [currentMode, setCurrentMode] = useState<'create' | 'review'>('create');
  const [templates, setTemplates] = useState<TemplateOption[]>([]);
  const [templatesLoading, setTemplatesLoading] = useState(false);
  const [templatesError, setTemplatesError] = useState<string | null>(null);
  const [selectedTemplateId, setSelectedTemplateId] = useState('');
  const [selectedLanguage, setSelectedLanguage] = useState<string>('');
  const [languageOptions, setLanguageOptions] = useState<
    Array<{ code: string; label: string }>
  >([]);
  const [formData, setFormData] = useState<FormDataShape>({
    ndaType: 'bilateral',
    xCompany: '',
    xAddress: '',
    xMail: '',
    zurichAddress: '',
    zurichMail: '',
    project: '',
    date: '',
    xSigner: '',
    zurichSigner: '',
    showDraftingNote: false
  });

  useEffect(() => {
    const load = async (): Promise<void> => {
      setTemplatesLoading(true);
      setTemplatesError(null);
      try {
        const list = await ndaApi.getTemplates();
        const codeFromName = (name: string): string | undefined => {
          const m =
            /-(en|de)\.(docx|doc)$/i.exec(name) || /-(en|de)$/i.exec(name);
          const c = m?.[1]?.toLowerCase();
          return c && (c === 'en' || c === 'de') ? c : undefined;
        };
        const labelForLang = (code?: string): string | undefined => {
          if (!code) return undefined;
          if (code === 'en') return 'English';
          if (code === 'de') return 'Deutsch';
          return code.toUpperCase();
        };
        const normalized: TemplateOption[] = list.map((t: any) => ({
          id: t.id,
          label: String(t.name ?? t.filename ?? t.id),
          ndaType: (t.type ||
            (/uni/i.test(
              `${String(t.id)}${String(t.name || '')}${String(
                t.filename || ''
              )}`
            )
              ? 'unilateral'
              : 'bilateral')) as NdaType,
          languageCode:
            t.languageCode ??
            codeFromName(String(t.filename ?? t.name ?? '')) ??
            undefined,
          languageLabel:
            t.languageLabel ??
            labelForLang(
              t.languageCode ?? codeFromName(String(t.filename ?? t.name ?? ''))
            )
        }));
        setTemplates(normalized);
        if (normalized.length > 0) setSelectedTemplateId(normalized[0].id);
        const langMap = new Map<string, string>();
        for (const t of normalized) {
          const code = t.languageCode ?? 'en';
          const label = t.languageLabel ?? labelForLang(code) ?? code;
          if (!langMap.has(code)) langMap.set(code, label);
        }
        const langs = Array.from(langMap.entries()).map(([code, label]) => ({
          code,
          label
        }));
        setLanguageOptions(langs);
        if (!selectedLanguage) setSelectedLanguage(langs[0]?.code ?? 'en');
      } catch (e: any) {
        setTemplatesError(e?.message ?? 'Failed to load templates');
      } finally {
        setTemplatesLoading(false);
      }
    };
    load();
  }, []);

  const selectedTemplate = useMemo(
    () => templates.find((t) => t.id === selectedTemplateId) ?? null,
    [templates, selectedTemplateId]
  );
  const visibleTemplates = useMemo(() => {
    if (!selectedLanguage) return templates;
    const filtered = templates.filter(
      (t) => (t.languageCode ?? 'en') === selectedLanguage
    );
    return filtered.length > 0 ? filtered : templates;
  }, [templates, selectedLanguage]);
  useEffect(() => {
    if (!selectedTemplate) return;
    const templateLang = selectedTemplate.languageCode ?? 'en';
    if (templateLang !== selectedLanguage) {
      setSelectedLanguage(templateLang);
    }
  }, [selectedTemplate]);
  useEffect(() => {
    if (selectedTemplate)
      setFormData((f: FormDataShape) => ({
        ...f,
        ndaType: selectedTemplate.ndaType
      }));
  }, [selectedTemplate]);

  const isFormValid = useMemo(
    () =>
      requiredKeys.every((k) =>
        Boolean((formData as any)[k]?.toString().trim())
      ),
    [formData]
  );
  const [showPreview, setShowPreview] = useState(false);

  const {
    messages: chatMessages,
    loading: chatLoading,
    inputRef: chatInputRef,
    inputPlaceholder,
    handleSend,
    handleSyncVariables
  } = useNdaChat({
    formData,
    selectedLanguage,
    selectedTemplateId,
    onApplyFields: (fields) =>
      setFormData((prev: FormDataShape) => ({ ...prev, ...fields }))
  });

  const [downloading, setDownloading] = useState(false);
  const [lastGeneratedText, setLastGeneratedText] = useState<string | null>(
    null
  );
  const [showFileNameModal, setShowFileNameModal] = useState(false);
  const [fileNameInput, setFileNameInput] = useState<string>('');
  const syncTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const debouncedSyncFormToChat = useCallback(
    (updatedFormData: FormDataShape) => {
      if (syncTimeoutRef.current) {
        clearTimeout(syncTimeoutRef.current);
      }
      syncTimeoutRef.current = setTimeout(() => {
        handleSyncVariables(updatedFormData, true); // Show response in chat
      }, 2000); // Sync after 1 second of inactivity
    },
    [handleSyncVariables]
  );

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (syncTimeoutRef.current) {
        clearTimeout(syncTimeoutRef.current);
      }
    };
  }, []);

  function downloadDocxFromGenerateResponse(
    res: {
      docxBase64?: string;
      docxFilename?: string;
      document?: string;
    },
    overrideName?: string
  ): void {
    const ensureExtension = (name: string, ext: string): string => {
      const trimmed = (name || '').trim();
      if (!trimmed) return `nda${ext}`;
      return trimmed.toLowerCase().endsWith(ext) ? trimmed : `${trimmed}${ext}`;
    };
    if (res.docxBase64) {
      const byteChars = globalThis.atob(res.docxBase64);
      const byteNums = new Array(byteChars.length);
      for (let i = 0; i < byteChars.length; i++) {
        byteNums[i] = byteChars.codePointAt(i) ?? 0;
      }
      const byteArray = new Uint8Array(byteNums);
      const blob = new Blob([byteArray], {
        type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const suggested = overrideName ?? res.docxFilename ?? 'nda.docx';
      a.download = ensureExtension(suggested, '.docx');
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      return;
    }
    const blob = new Blob([res.document ?? ''], {
      type: 'text/plain;charset=utf-8'
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = ensureExtension(overrideName ?? 'nda.txt', '.txt');
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  }

  const onDownload = async (): Promise<void> => {
    if (!isFormValid) {
      displayToastError('Please fill in all required fields');
      return;
    }
    const isSupported = languageOptions.some(
      (l) => l.code === selectedLanguage
    );
    if (!isSupported) {
      displayToastError('Selected language is not supported');
      return;
    }
    const baseName = `${formData.xCompany || 'nda'}-${
      formData.date || ''
    }`.trim();
    const suggestedName = baseName || 'nda.docx';
    setFileNameInput(suggestedName);
    setShowFileNameModal(true);
  };

  const confirmDownload = async (): Promise<void> => {
    try {
      setDownloading(true);
      dispatch(setGeneratingExtraction(true));
      const payload: any = {
        disclosing_party: formData.zurichSigner ?? 'Zurich Insurance',
        receiving_party: formData.xCompany,
        effective_date: formData.date,
        term_months: 24,
        jurisdiction: 'Switzerland',
        purpose: formData.project,
        confidentiality_scope: formData.showDraftingNote
          ? ['financial']
          : ['technical'],
        confidentiality_scope_text: formData.showDraftingNote
          ? 'financial'
          : 'technical',
        exclusions: [
          'publicKnowledge',
          'independentlyDeveloped',
          'thirdParty',
          'requiredByLaw'
        ],
        exclusions_text:
          'publicKnowledge, independentlyDeveloped, thirdParty, requiredByLaw',
        permitted_disclosures: ['legal_counsel'],
        publishPriceSensitiveInfo: formData.showDraftingNote === true,
        project_description: formData.project,
        counterparty_address: formData.xAddress ?? null,
        counterparty_email: formData.xMail ?? null,
        counterparty_signer: formData.xSigner ?? null,
        zurich_address: formData.zurichAddress ?? null,
        zurich_email: formData.zurichMail ?? null,
        zurich_signer: formData.zurichSigner ?? null,
        disclosure_nature: formData.ndaType,
        language_code: selectedLanguage,
        survival_period_months: null,
        template_id: selectedTemplateId ?? undefined,
        ndaType: formData.ndaType,
        xCompany: formData.xCompany,
        xAddress: formData.xAddress,
        xMail: formData.xMail,
        zurichAddress: formData.zurichAddress,
        zurichMail: formData.zurichMail,
        project: formData.project,
        date: formData.date,
        xSigner: formData.xSigner,
        zurichSigner: formData.zurichSigner,
        showDraftingNote: formData.showDraftingNote ?? false,
        templateId: selectedTemplateId ?? undefined
      };
      const res: GenerateNDAResponse = await ndaApi.generateNDA(payload);
      if (typeof (res as any).document === 'string') {
        setLastGeneratedText((res as any).document as string);
      }
      downloadDocxFromGenerateResponse(
        {
          docxBase64: res.docxBase64,
          docxFilename: res.docxFilename ?? res.fileName,
          document: (res as any).document
        },
        fileNameInput || undefined
      );
      notify({ type: 'success', message: 'NDA download started' });
    } catch (e: any) {
      displayToastError(e?.message ?? 'Failed to generate NDA document');
    } finally {
      setDownloading(false);
      dispatch(setGeneratingExtraction(false));
      setShowFileNameModal(false);
    }
  };

  return (
    <div className={styles.container}>
      <Header
        currentMode={currentMode}
        setCurrentMode={setCurrentMode}
        setShowPreview={setShowPreview}
      />
      <div className={styles.content}>
        {currentMode === 'create' ? (
          <>
            <CreateMode
              templates={templates}
              visibleTemplates={visibleTemplates}
              templatesLoading={templatesLoading}
              templatesError={templatesError}
              selectedTemplateId={selectedTemplateId}
              onSelectTemplateId={setSelectedTemplateId}
              selectedTemplate={selectedTemplate}
              selectedLanguage={selectedLanguage}
              languageOptions={languageOptions}
              onSelectLanguage={setSelectedLanguage}
              formData={formData}
              setFormData={setFormData}
              onFieldChange={(key, value) => {
                // Update form data
                const updatedFormData = { ...formData, [key]: value };
                // Debounced sync to chat
                debouncedSyncFormToChat(updatedFormData);
              }}
              isFormValid={isFormValid}
              showPreview={showPreview}
              setShowPreview={setShowPreview}
              downloading={downloading}
              onDownload={onDownload}
            />
            <ChatPanel
              messages={chatMessages}
              loading={chatLoading}
              inputRef={chatInputRef}
              placeholder={inputPlaceholder}
              onSend={handleSend}
            />
          </>
        ) : (
          <ReviewMode generatedDocumentText={lastGeneratedText ?? undefined} />
        )}
      </div>
      <GeneratingExtraction />
      {showFileNameModal && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0,0,0,0.35)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
            padding: 16
          }}
        >
          <dialog
            open
            aria-label="Enter file name"
            style={{
              border: 'none',
              borderRadius: 8,
              padding: 16,
              width: '100%',
              maxWidth: 480,
              boxSizing: 'border-box',
              maxHeight: '90vh',
              boxShadow: '0 8px 24px rgba(0,0,0,0.2)'
            }}
          >
            <h3 style={{ margin: '0 0 12px' }}>Choose file name</h3>
            <input
              type="text"
              value={fileNameInput}
              onChange={(e) => setFileNameInput(e.target.value)}
              style={{
                width: '100%',
                padding: '10px 12px',
                border: '1px solid #d0d7de',
                borderRadius: 6,
                marginBottom: 12,
                boxSizing: 'border-box'
              }}
              placeholder="nda.docx"
            />
            <div
              style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}
            >
              <button
                type="button"
                onClick={() => setShowFileNameModal(false)}
                style={{
                  padding: '8px 12px',
                  borderRadius: 6,
                  border: '1px solid #d0d7de',
                  background: '#fff'
                }}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={confirmDownload}
                style={{
                  padding: '8px 12px',
                  borderRadius: 6,
                  border: '1px solid #0969da',
                  background: '#0969da',
                  color: '#fff'
                }}
              >
                Download
              </button>
            </div>
          </dialog>
        </div>
      )}
    </div>
  );
};

export default NDAGenerator;
